import React, { useState } from "react";
import Footer from "./Footer";
import { LifeBuoy, CircleCheck } from "lucide-react";
import { Snackbar, Alert } from "@mui/material";
import { useEKYCManagerService } from "../services/useEKYCManagerService";
import { flushDiagnostics, trackButton } from "../../../diagnostics";

// Shown by EKYCManager after 3 document-scan failures or 2 server timeouts.
// Diagnostics are collected regardless; clicking here records that the user
// asked for help and returns a reference code they can quote.
const ContactSupport = ({ referenceCode, counts = {}, onContinue }) => {
  const { contactSupport } = useEKYCManagerService();
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [reference, setReference] = useState(referenceCode || null);
  const [toast, setToast] = useState({ open: false, severity: "success", msg: "" });

  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  const handleContact = async () => {
    if (isSending) return;
    setIsSending(true);
    trackButton("contact_support", {
      serverTimeoutCount: counts.serverTimeoutCount,
      docScanFailCount: counts.docScanFailCount,
    });
    // Ship whatever is buffered right now so support has the latest context.
    flushDiagnostics();
    try {
      const res = await contactSupport(referenceCode, {
        reason: "user_requested",
        serverTimeoutCount: counts.serverTimeoutCount || 0,
        docScanFailCount: counts.docScanFailCount || 0,
      });
      if (res && res.status === "success") {
        setReference((res.data && res.data.referenceCode) || referenceCode || null);
        setSent(true);
        setToast({ open: true, severity: "success", msg: "Support request sent." });
      } else {
        setToast({ open: true, severity: "error", msg: "Couldn't send your request. Please try again." });
      }
    } catch (e) {
      setToast({ open: true, severity: "error", msg: "Couldn't send your request. Please try again." });
    } finally {
      setIsSending(false);
    }
  };

  const handleContinue = () => {
    trackButton("continue_trying");
    if (onContinue) onContinue();
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 flex justify-center items-center">
      <div className="bg-white h-full flex flex-col justify-start p-4 min-h-screen w-full max-w-md">
        <div className="bg-gray-100 p-8 rounded-2xl mt-[calc(100vh/16)] mb-8">
          <div className="flex flex-col items-center mb-6">
            {sent ? (
              <CircleCheck height="64" width="64" color="green" />
            ) : (
              <LifeBuoy height="64" width="64" color="#ef4444" />
            )}
          </div>

          {!sent ? (
            <div className="flex flex-col">
              <p className="text-2xl font-medium">Having trouble?</p>
              <p className="text-gray-500 text-md mt-4">
                We're sorry — we've run into repeated problems completing your verification.
                You can send the technical details to our support team so we can look into it.
              </p>
            </div>
          ) : (
            <div className="flex flex-col">
              <p className="text-2xl font-medium">Support request sent</p>
              <p className="text-gray-500 text-md mt-4">
                Thanks — our team has received your details. Please quote this reference if you reach out:
              </p>
              {reference ? (
                <div className="mt-4 bg-white border border-gray-200 rounded-lg p-3 text-center">
                  <span className="text-xs text-gray-400 block">Reference code</span>
                  <span className="font-mono text-sm break-all">{reference}</span>
                </div>
              ) : null}
            </div>
          )}

          <div className="flex flex-col gap-3 mt-8">
            {!sent ? (
              <button
                type="button"
                data-diag="contact-support-send"
                onClick={handleContact}
                disabled={isSending}
                className="w-full bg-red-500 text-white rounded-lg py-3 font-medium disabled:opacity-60 flex items-center justify-center"
              >
                {isSending ? "Sending…" : "Contact support"}
              </button>
            ) : null}

            <button
              type="button"
              data-diag="contact-support-continue"
              onClick={handleContinue}
              disabled={isSending}
              className="w-full bg-transparent text-gray-600 rounded-lg py-3 font-medium border border-gray-300 disabled:opacity-60"
            >
              {sent ? "Back to verification" : "Continue trying"}
            </button>
          </div>
        </div>

        <div className="flex flex-1" />
        <Footer />
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ width: "100%" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ContactSupport;
